// In-flight request coalescing (2026-08-09).
//
// Measured on the live Desk: entering the tab fired 48 core requests, three of them
// the SAME `access-requests` GET issued concurrently by three components, with
// `desk-event` and `geocode` doubled the same way. Many of those 48 landed within a
// millisecond of each other at ~2,493 ms — they were QUEUED behind one another, not
// individually slow, so every redundant request delays a real one.
//
// ⚠️ This is NOT a cache and must never become one. It holds a promise only while
// that request is actually in flight, and drops it the moment the request settles.
// Two components asking the same question at the same moment get one answer; the
// next ask is a fresh read. That is what keeps the platform's standing rule — an
// agent sees truth, never a stale copy — while removing the duplicates outright.
// A TTL here would trade the rule for milliseconds; don't.
export function makeCoalescer() {
  const inflight = new Map()

  function coalesced(key, run) {
    const hit = inflight.get(key)
    if (hit) return hit
    const p = run()
    inflight.set(key, p)
    // Clear on settle, success OR failure. A failed read must not be replayed to a
    // later caller, and a shared rejection must not surface as one component's
    // error appearing inside another.
    p.then(() => { if (inflight.get(key) === p) inflight.delete(key) },
           () => { if (inflight.get(key) === p) inflight.delete(key) })
    return p
  }

  coalesced.inflightCount = () => inflight.size
  return coalesced
}

// Split ids into request-sized chunks. ~37 chars per uuid keeps an `in.(…)` request
// line under the ~8 KB nginx cap that already governs the Desk's other batch reads;
// an unchunked in.() over a full roster is the shape that failed SILENTLY to an
// empty list in B567.
export function chunkIds(ids, size = 80) {
  const out = []
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size))
  return out
}
